import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, Loader2, CheckCircle2, AlertCircle, Play, Wand2, ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

/**
 * AI-assisted calculated measure — guided, approve-before-commit loop:
 *  1. describe → up to 3 validated candidate formulas (with a "how it differs" note)
 *  2. pick one → Test against data (real sample rows) and/or revise in plain English
 *  3. Approve → creates a draft (uncertified) measure with an editable name + unit.
 */
export function AiFormulaDialog({ open, onOpenChange, modelId, textFields = [], dateFields = [], allFields = [], onApproved, initialDescription = '' }) {
  const [desc, setDesc] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [candidates, setCandidates] = React.useState(null); // array | null
  const [genError, setGenError] = React.useState('');
  const [selected, setSelected] = React.useState(null); // candidate object
  const [name, setName] = React.useState('');
  const [unit, setUnit] = React.useState('');
  const [changeReq, setChangeReq] = React.useState('');
  const [testing, setTesting] = React.useState(false);
  const [testRun, setTestRun] = React.useState(null);
  const [approving, setApproving] = React.useState(false);

  const reset = () => {
    setDesc(''); setCandidates(null); setGenError(''); setSelected(null);
    setName(''); setUnit(''); setChangeReq(''); setTestRun(null);
  };

  // Seed the description when opened from a hand-off (e.g. AI Explore).
  React.useEffect(() => {
    if (open && initialDescription) setDesc(initialDescription);
     
  }, [open, initialDescription]);

  const generate = async () => {
    if (!desc.trim()) { toast.error('Describe what the formula should calculate'); return; }
    setLoading(true); setGenError(''); setCandidates(null); setSelected(null); setTestRun(null);
    try {
      const res = await api.generateFormulaCandidates({ description: desc, model_id: modelId });
      if (res.error) setGenError(res.error);
      setCandidates(res.candidates || []);
    } catch (e) { toast.error(e.message || 'AI formula generation failed'); }
    finally { setLoading(false); }
  };

  const pick = (c) => {
    setSelected(c); setName(c.name || ''); setUnit(c.unit || ''); setTestRun(null); setChangeReq('');
  };

  const revise = async () => {
    if (!changeReq.trim() || !selected) return;
    setLoading(true); setTestRun(null);
    try {
      const res = await api.generateFormulaCandidates({ description: desc, model_id: modelId, current_formula: selected.formula, change_request: changeReq });
      const next = (res.candidates || [])[0];
      if (next) { setSelected(next); setName(next.name || name); setUnit(next.unit || unit); setChangeReq(''); }
      else toast.error(res.error || 'Could not revise the formula');
    } catch (e) { toast.error(e.message || 'Revision failed'); }
    finally { setLoading(false); }
  };

  const runTest = async () => {
    if (!selected) return;
    setTesting(true); setTestRun(null);
    try {
      const comp = await api.testMeasureFormula(modelId, { name: name || 'Test Measure', formula: selected.formula });
      if (!comp.ok) { setTestRun({ ok: false, error: comp.error }); return; }
      const dim = textFields[0] || dateFields[0] || allFields.find(f => f.semantic_type === 'dimension');
      const dims = dim ? [dim.name] : [];
      const col = name || 'Test Measure';
      const res = await api.executeQuery({ model_id: modelId, dimensions: dims, measures: [col], calculations: [comp.spec], limit: 3 });
      const rows = (res.data || []).slice(0, 3);
      setTestRun({ ok: true, rows, columns: dims.length ? [dims[0], col] : [col] });
    } catch (e) { setTestRun({ ok: false, error: 'Compiled OK but failed to run: ' + (e?.message || e) }); }
    finally { setTesting(false); }
  };

  const approve = async () => {
    if (!selected || !name.trim()) { toast.error('Give the measure a name'); return; }
    setApproving(true);
    try {
      const created = await api.createSemanticMeasure(modelId, { name: name.trim(), formula: selected.formula, description: selected.description || '', unit: unit || '' });
      toast.success(`Created draft measure "${name.trim()}"`);
      if (onApproved) onApproved(created);
      onOpenChange(false); reset();
    } catch (e) { toast.error(e.message || 'Could not create the measure'); }
    finally { setApproving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg" data-testid="ai-formula-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-400" /> AI-assisted measure
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Step 1 — describe */}
          <div>
            <Label className="text-xs text-muted-foreground">Describe what you want to calculate</Label>
            <textarea
              value={desc}
              onChange={e => setDesc(e.target.value)}
              placeholder="e.g. loss ratio"
              className="w-full rounded-lg bg-muted/30 border border-border/30 text-sm text-foreground placeholder:text-muted-foreground p-3 mt-1 min-h-[64px] resize-none outline-none focus:ring-1 focus:ring-primary/50"
              data-testid="ai-formula-description"
            />
          </div>
          {!selected && (
            <Button onClick={generate} disabled={loading || !desc.trim()} className="w-full rounded-lg" data-testid="ai-formula-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {candidates ? 'Regenerate' : 'Generate formula'}
            </Button>
          )}

          {genError && !selected && (
            <div className="flex items-start gap-1.5 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2" data-testid="ai-formula-error">
              <AlertCircle className="w-3.5 h-3.5 mt-px flex-shrink-0" /><span>{genError}</span>
            </div>
          )}

          {/* Step 2a — candidate list */}
          {candidates && !selected && candidates.length > 0 && (
            <div className="space-y-2" data-testid="ai-formula-candidates">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{candidates.length > 1 ? `${candidates.length} ways to calculate this — pick one` : 'Suggested formula'}</p>
              {candidates.map((c, i) => (
                <button key={i} onClick={() => pick(c)} className="w-full text-left p-3 rounded-lg bg-[#0d1117] border border-border/40 hover:border-indigo-500/50 transition-colors" data-testid={`ai-formula-candidate-${i}`}>
                  <p className="text-xs text-foreground/90">{c.description || c.name}</p>
                  <p className="text-sm font-mono text-emerald-300 mt-1 break-words">{c.formula}</p>
                  {c.difference_note && <p className="text-[10px] text-indigo-300/80 mt-1">↳ {c.difference_note}</p>}
                </button>
              ))}
            </div>
          )}

          {/* Step 2b/3 — selected candidate: test, revise, approve */}
          {selected && (
            <div className="space-y-3" data-testid="ai-formula-selected">
              <button onClick={() => { setSelected(null); setTestRun(null); }} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="ai-formula-back">
                <ArrowLeft className="w-3 h-3" /> back to options
              </button>
              <div className="p-3 rounded-lg bg-[#0d1117] border border-indigo-500/30">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Formula</p>
                <p className="text-sm font-mono text-emerald-300 break-words">{selected.formula}</p>
                {selected.description && <p className="text-xs text-foreground/70 mt-1.5">{selected.description}</p>}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Name</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} className="h-7 !text-xs rounded-lg mt-0.5" data-testid="ai-formula-name" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Unit</Label>
                  <Input value={unit} onChange={e => setUnit(e.target.value)} placeholder="USD / percent / ratio" className="h-7 !text-xs rounded-lg mt-0.5" data-testid="ai-formula-unit" />
                </div>
              </div>

              {/* Test against data */}
              <div>
                <Button variant="outline" size="sm" onClick={runTest} disabled={testing} className="rounded-lg text-[11px] h-7" data-testid="ai-formula-test-btn">
                  {testing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                  {testing ? 'Testing…' : 'Test against data'}
                </Button>
                {testRun && (
                  <div className="mt-1.5" data-testid="ai-formula-test-result">
                    {testRun.ok === false ? (
                      <div className="flex items-start gap-1.5 text-[10px] text-rose-400"><AlertCircle className="w-3 h-3 mt-px flex-shrink-0" /><span>{testRun.error}</span></div>
                    ) : (testRun.rows && testRun.rows.length ? (
                      <div className="rounded-lg border border-emerald-500/20 overflow-hidden">
                        <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-400 text-[9px]"><CheckCircle2 className="w-3 h-3" />Valid — {testRun.rows.length} sample record{testRun.rows.length > 1 ? 's' : ''}</div>
                        <table className="w-full text-[10px]"><tbody>
                          {testRun.rows.map((r, i) => (
                            <tr key={i} className="border-t border-border/20">
                              {(testRun.columns || Object.keys(r)).map(c => (
                                <td key={c} className="px-2 py-0.5 font-mono text-foreground">{typeof r[c] === 'number' ? Number(r[c].toFixed(4)).toLocaleString() : String(r[c] ?? '—')}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody></table>
                      </div>
                    ) : (
                      <div className="flex items-start gap-1.5 text-[10px] text-emerald-400"><CheckCircle2 className="w-3 h-3 mt-px flex-shrink-0" /><span>Formula is valid (no sample rows returned).</span></div>
                    ))}
                  </div>
                )}
              </div>

              {/* Revise in plain English */}
              <div>
                <Label className="text-[10px] text-muted-foreground">Refine in plain English</Label>
                <div className="flex gap-2 mt-0.5">
                  <Input value={changeReq} onChange={e => setChangeReq(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') revise(); }} placeholder="e.g. use earned premium instead" className="h-7 !text-xs rounded-lg" data-testid="ai-formula-revise-input" />
                  <Button variant="outline" size="sm" onClick={revise} disabled={loading || !changeReq.trim()} className="rounded-lg text-[11px] h-7 flex-shrink-0" data-testid="ai-formula-revise-btn">
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  </Button>
                </div>
              </div>

              <Button onClick={approve} disabled={approving || !name.trim()} className="w-full rounded-lg" data-testid="ai-formula-approve-btn">
                {approving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                Approve &amp; create draft measure
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
